import { useEffect, useRef, useState } from "react";
import { captureExperimentExposure, captureGrowthEvent, observeVariant } from "./analytics";
import { controlOf, type ExperimentKey, type Variant } from "./experiments";

/**
 * The variant for one experiment, plus the exposure event that says a visitor
 * actually reached the thing being tested.
 *
 * Exposure is reported from here rather than from each screen, so an arm cannot
 * be rendered without being counted — the failure that makes an experiment
 * readable and wrong. It fires once per arm per page session; a flag that
 * resolves late and changes the arm reports the new one.
 *
 * `resolved` stays false until the flag answers or the deadline passes. A
 * screen that would flash the control arm and then swap should render a
 * placeholder while it is false; one that can swap for free can ignore it.
 */
export function useExperiment<K extends ExperimentKey>(
  key: K,
  { track = true }: { track?: boolean } = {},
) {
  const [state, setState] = useState(() => ({ variant: controlOf(key), resolved: false }));
  const exposed = useRef<string>(undefined);

  useEffect(
    () => observeVariant(key, (variant) => setState({ variant, resolved: true })),
    [key],
  );

  useEffect(() => {
    if (!track || !state.resolved) return;
    const exposure = `${key}:${state.variant}`;
    if (exposed.current === exposure) return;
    exposed.current = exposure;
    captureExperimentExposure(key, state.variant);
    captureGrowthEvent("experiment_exposed", { experiment: key, variant: state.variant });
  }, [key, state, track]);

  return state as { variant: Variant<K>; resolved: boolean };
}
